import React from 'react';
import { RotateCcw } from 'lucide-react';
import { Panel, Section, Sub, Tile, Slider, NumInput, Badge } from './GrowthBits';
import { ASSUMPTIONS, DEFAULTS, FUNNEL_STEPS, FUNNEL_RATES, budget, revenue, money, num } from './growthData';

const SHADES = ['#4a5565', '#5f6b80', '#7a879c', '#9aa6ba', '#0a84ff', '#64d2ff', '#30d158'];

export default function ReverseTab({ S, D, update }) {
  const a = S.a, rf = D.rf, need = D.needT;
  const rows = [['Associate MBs', need.assoc, S.h.assoc, 'assoc'], ['Mortgage Brokers', need.broker, S.h.broker, 'broker'], ['Credit Assessors', need.assessor, S.h.assessor, 'assessor']];
  return (
    <div data-testid="egt-tab-reverse">
      <Section eyebrow="Part 02 · Reverse-Engineer the Target" title="What has to happen to hit the number" lead="Set the target and the assumptions; the funnel, team and budget recalculate instantly. Every figure here is a planning projection, not live performance." />

      <Sub note="Editable planning assumptions" right={<button type="button" className="gpj-btn sm" onClick={() => update({ a: { ...DEFAULTS } })} data-testid="egt-reset-assumptions"><RotateCcw /> Reset to defaults</button>}>Assumptions</Sub>
      <Panel testid="egt-assumptions">
        <div className="egt-ctrls">
          {ASSUMPTIONS.map((c) => c.input
            ? <NumInput key={c.k} label={c.label} value={a[c.k]} min={c.min} onChange={(v) => update({ a: { ...a, [c.k]: v } })} testid={`egt-a-${c.k}`} />
            : <Slider key={c.k} label={c.label} value={a[c.k]} min={c.min} max={c.max} step={c.step || 1} fmt={c.fmt || (c.pct ? (v) => `${v}%` : undefined)} onChange={(v) => update({ a: { ...a, [c.k]: v } })} tone={c.k === 'target' ? 'var(--green)' : undefined} testid={`egt-a-${c.k}`} />)}
        </div>
      </Panel>

      <Sub note={`Per month · to lodge $${a.target}M`}>Required funnel</Sub>
      <div className="gpj-grid gpj-hero">
        <Panel testid="egt-funnel-panel">
          <div className="egt-funnel">
            {FUNNEL_STEPS.map(([label, k], i) => (
              <div key={k} className="egt-frow" style={{ animationDelay: `${i * 60}ms` }}>
                <span>{label}</span>
                <div className="egt-fbar"><i style={{ width: `${18 + (rf[k] / rf.leads) * 82}%`, '--c': SHADES[i] }}>{num(rf[k])}</i></div>
                <em>{i < 6 ? `${a[FUNNEL_RATES[i]]}% ↓` : ''}</em>
              </div>
            ))}
          </div>
        </Panel>
        <div className="egt-tiles" style={{ gridTemplateColumns: 'repeat(2,minmax(0,1fr))', alignContent: 'start' }}>
          <Tile label="Leads / week" value={num(rf.leads / 4.33)} src="Calc" testid="egt-rv-leadsW" />
          <Tile label="Lodgements / week" value={num(rf.lodge / 4.33)} src="Calc" tone="ok" testid="egt-rv-lodgeW" />
          <Tile label="Marketing budget / mo" value={money(budget(a, rf))} src="Calc" sub={<>{a.paid}% paid × ${a.cpl}/lead</>} testid="egt-rv-budget" />
          <Tile label="Est. revenue / mo" value={money(revenue(a, rf))} src="Estimate" sub="0.65% upfront + trail" testid="egt-rv-rev" />
        </div>
      </div>

      <Sub note="Hiring is triggered by demand, not dates">Team required vs current</Sub>
      <div className="gpj-grid gpj-hero">
        <Panel testid="egt-team-table">
          <div className="gpj-reg-wrap"><table className="gpj-reg" style={{ minWidth: 420 }}>
            <thead><tr><th>Role</th><th className="r">Required</th><th className="r">Current</th><th className="r">Gap</th><th>Status</th></tr></thead>
            <tbody>{rows.map(([role, req, cur]) => { const gap = req - cur; return (
              <tr key={role} style={{ cursor: 'default' }}><td><strong>{role}</strong></td><td className="r">{req}</td><td className="r">{cur}</td><td className={`r ${gap > 0 ? 'amber' : ''}`}>{gap > 0 ? `+${gap}` : gap < 0 ? <span className="ok">surplus {-gap}</span> : '0'}</td><td>{gap <= 0 ? <Badge tone="green">covered</Badge> : <Badge tone="amber">hire {gap}</Badge>}</td></tr>
            ); })}</tbody>
          </table></div>
        </Panel>
        <Panel title="Current headcount" sub="Who's on the team today">
          <div className="egt-ctrls">
            <NumInput label="Current associates" value={S.h.assoc} onChange={(v) => update({ h: { ...S.h, assoc: v } })} testid="egt-h-assoc" />
            <NumInput label="Current brokers" value={S.h.broker} onChange={(v) => update({ h: { ...S.h, broker: v } })} testid="egt-h-broker" />
            <NumInput label="Current credit assessors" value={S.h.assessor} onChange={(v) => update({ h: { ...S.h, assessor: v } })} testid="egt-h-assessor" />
          </div>
        </Panel>
      </div>
      <div className="egt-note">Projections only. Conversion rates and capacities are editable planning assumptions; revenue uses market-standard commission estimates (0.65% upfront, 0.15% trail), not confirmed figures.</div>
    </div>
  );
}
